import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/hooks/useCart';

type OrderStatus = 'form' | 'success';

export default function CheckoutPage() {
  const { cartItems, totalPrice, clearCart } = useCart();
  const navigate = useNavigate();
  const [status, setStatus] = useState<OrderStatus>('form');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', address: '' });
  const [errors, setErrors] = useState<Partial<typeof form>>({});
  const [paymentMethod, setPaymentMethod] = useState<'bank_transfer'>('bank_transfer');

  const shippingFee = totalPrice >= 1000 ? 0 : 100;
  const finalTotal = totalPrice + shippingFee;

  const validate = () => {
    const newErrors: Partial<typeof form> = {};
    if (!form.name.trim()) newErrors.name = '請填寫姓名';
    if (!form.phone.trim()) newErrors.phone = '請填寫電話';
    else if (!/^[0-9]{8,10}$/.test(form.phone.replace(/-/g, ''))) newErrors.phone = '請填寫正確電話格式';
    if (!form.address.trim()) newErrors.address = '請填寫地址';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;
    if (cartItems.length === 0) return;

    setIsSubmitting(true);

    const orderItems = cartItems
      .map((item) => `${item.product.name} x${item.quantity} NT$${item.product.price * item.quantity}`)
      .join(' | ');

    const formData = new URLSearchParams();
    formData.append('name', form.name);
    formData.append('phone', form.phone);
    formData.append('address', form.address);
    formData.append('order_items', orderItems);
    formData.append('total_amount', `NT$ ${finalTotal}`);

    try {
      await fetch('https://readdy.ai/api/form/d798n6idqn9mib2tr1og', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
    } catch (_) {
      // silent
    }

    clearCart();
    setStatus('success');
    setIsSubmitting(false);
  };

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl p-10 max-w-md w-full text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-green-50 rounded-full mx-auto mb-5">
            <i className="ri-checkbox-circle-line text-4xl text-green-500"></i>
          </div>
          <h2 className="text-2xl font-black text-[#1a1a1a] mb-3">訂單已送出，請匯款</h2>
          <p className="text-gray-500 text-sm leading-relaxed mb-2">
            感謝您的訂購！我們已收到您的訂單。
          </p>
          <p className="text-gray-500 text-sm leading-relaxed mb-8">
            請依照以下帳號完成匯款，匯款後請來電或傳訊通知我們，我們將盡快為您安排出貨。
          </p>
          <div className="bg-[#FAF8F5] rounded-xl p-5 text-left mb-8">
            <p className="text-xs font-bold text-[#8B4513] uppercase tracking-widest mb-3">匯款資訊</p>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">銀行</span>
                <span className="font-bold text-[#1a1a1a]">土地銀行（005）</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">帳號</span>
                <span className="font-bold text-[#1a1a1a]">116001012230</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">戶名</span>
                <span className="font-bold text-[#1a1a1a]">元益有限公司</span>
              </div>
            </div>
          </div>
          <button
            onClick={() => navigate('/')}
            className="w-full bg-[#8B4513] text-white py-3.5 rounded-full font-bold text-sm hover:bg-[#6B3410] transition-colors cursor-pointer whitespace-nowrap"
          >
            回到首頁
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-gray-500 hover:text-[#8B4513] transition-colors cursor-pointer"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-left-line text-lg"></i>
            </span>
            <span className="text-sm font-medium">返回購物</span>
          </button>
          <h1 className="text-lg font-black text-[#1a1a1a]">確認訂單</h1>
          <div className="w-20" />
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {cartItems.length === 0 ? (
          <div className="text-center py-20">
            <span className="w-20 h-20 flex items-center justify-center mx-auto mb-4">
              <i className="ri-shopping-cart-2-line text-6xl text-gray-200"></i>
            </span>
            <p className="text-gray-400 text-base font-medium mb-6">購物車是空的</p>
            <button
              onClick={() => navigate('/')}
              className="px-8 py-3 bg-[#8B4513] text-white rounded-full font-bold text-sm hover:bg-[#6B3410] transition-colors cursor-pointer whitespace-nowrap"
            >
              去選購商品
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left: Form */}
            <div className="lg:col-span-3 space-y-4">
              {/* Step 1: 收件資訊 */}
              <form
                id="checkout-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-white rounded-2xl p-6 md:p-8"
              >
                <div className="flex items-center space-x-2 mb-6">
                  <span className="w-6 h-6 flex items-center justify-center bg-[#8B4513] text-white text-xs font-black rounded-full">1</span>
                  <h2 className="text-base font-black text-[#1a1a1a]">收件資訊</h2>
                </div>
                <div className="space-y-5">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-bold text-[#1a1a1a] mb-1.5">
                      姓名 <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="請輸入您的姓名"
                      className={`w-full border rounded-lg px-4 py-3 text-sm text-[#1a1a1a] placeholder-gray-300 outline-none transition-colors ${
                        errors.name ? 'border-red-300 bg-red-50' : 'border-gray-200 focus:border-[#8B4513]'
                      }`}
                    />
                    {errors.name && <p className="text-xs text-red-400 mt-1">{errors.name}</p>}
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-bold text-[#1a1a1a] mb-1.5">
                      電話 <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="請輸入您的聯絡電話"
                      className={`w-full border rounded-lg px-4 py-3 text-sm text-[#1a1a1a] placeholder-gray-300 outline-none transition-colors ${
                        errors.phone ? 'border-red-300 bg-red-50' : 'border-gray-200 focus:border-[#8B4513]'
                      }`}
                    />
                    {errors.phone && <p className="text-xs text-red-400 mt-1">{errors.phone}</p>}
                  </div>

                  {/* Address */}
                  <div>
                    <label className="block text-sm font-bold text-[#1a1a1a] mb-1.5">
                      收件地址 <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                      placeholder="請輸入完整收件地址"
                      className={`w-full border rounded-lg px-4 py-3 text-sm text-[#1a1a1a] placeholder-gray-300 outline-none transition-colors ${
                        errors.address ? 'border-red-300 bg-red-50' : 'border-gray-200 focus:border-[#8B4513]'
                      }`}
                    />
                    {errors.address && <p className="text-xs text-red-400 mt-1">{errors.address}</p>}
                  </div>
                </div>

                {/* Submit button (mobile) */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="lg:hidden w-full mt-8 bg-[#8B4513] text-white py-4 rounded-full font-bold text-sm hover:bg-[#6B3410] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {isSubmitting ? '送出中...' : '確認下單'}
                </button>
              </form>

              {/* Step 2: 付款方式 */}
              <div className="bg-white rounded-2xl p-6 md:p-8">
                <div className="flex items-center space-x-2 mb-6">
                  <span className="w-6 h-6 flex items-center justify-center bg-[#8B4513] text-white text-xs font-black rounded-full">2</span>
                  <h2 className="text-base font-black text-[#1a1a1a]">付款方式</h2>
                </div>

                {/* Bank Transfer Card */}
                <div
                  onClick={() => setPaymentMethod('bank_transfer')}
                  className={`flex items-center space-x-4 border-2 rounded-xl p-4 cursor-pointer transition-all hover:border-blue-400 ${
                    paymentMethod === 'bank_transfer'
                      ? 'border-blue-500 bg-blue-50/40'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  {/* Icon */}
                  <div className={`w-11 h-11 flex items-center justify-center rounded-lg flex-shrink-0 transition-colors ${
                    paymentMethod === 'bank_transfer' ? 'bg-blue-100' : 'bg-gray-100'
                  }`}>
                    <i className={`ri-bank-line text-xl transition-colors ${
                      paymentMethod === 'bank_transfer' ? 'text-blue-600' : 'text-gray-400'
                    }`}></i>
                  </div>

                  {/* Text */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-black text-[#1a1a1a] mb-0.5">銀行轉帳（先付款後發貨）</p>
                    <p className="text-xs text-gray-400 leading-relaxed mb-1.5">
                      訂單成立後請於 3 天內完成匯款，確認後才會安排出貨
                    </p>
                    <p className="text-xs font-bold text-red-500">
                      應付金額：NT$ {finalTotal}
                    </p>
                  </div>

                  {/* Radio */}
                  <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                      paymentMethod === 'bank_transfer' ? 'border-blue-500' : 'border-gray-300'
                    }`}>
                      {paymentMethod === 'bank_transfer' && (
                        <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Order Summary */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl p-6 md:p-8 sticky top-6">
                <h2 className="text-base font-black text-[#1a1a1a] mb-5">訂單明細</h2>

                <div className="space-y-3 mb-5">
                  {cartItems.map((item) => (
                    <div key={item.product.id} className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-[#FAF8F5] rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={item.product.image}
                          alt={item.product.name}
                          className="w-full h-full object-contain p-1"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-[#1a1a1a] truncate">{item.product.name}</p>
                        <p className="text-xs text-gray-400">x {item.quantity}</p>
                      </div>
                      <span className="text-sm font-black text-[#1a1a1a] whitespace-nowrap">
                        NT$ {item.product.price * item.quantity}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-gray-100 pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">小計</span>
                    <span className="font-medium text-[#1a1a1a]">NT$ {totalPrice}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">運費</span>
                    <span className={shippingFee === 0 ? 'text-green-600 font-medium' : 'font-medium text-[#1a1a1a]'}>
                      {shippingFee === 0 ? '免運費' : `NT$ ${shippingFee}`}
                    </span>
                  </div>
                  {totalPrice < 1000 && (
                    <p className="text-xs text-gray-400">再購買 NT$ {1000 - totalPrice} 即可享免運費</p>
                  )}
                </div>

                <div className="border-t border-gray-100 mt-4 pt-4 flex justify-between items-center mb-6">
                  <span className="font-bold text-[#1a1a1a]">總計</span>
                  <span className="text-xl font-black text-[#8B4513]">NT$ {finalTotal}</span>
                </div>

                {/* Submit button (desktop) */}
                <button
                  type="submit"
                  form="checkout-form"
                  disabled={isSubmitting}
                  className="hidden lg:block w-full bg-[#8B4513] text-white py-4 rounded-full font-bold text-sm hover:bg-[#6B3410] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {isSubmitting ? '送出中...' : '確認下單'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="max-w-5xl mx-auto px-4 py-8 border-t border-gray-200">
        <p className="text-center text-gray-400 text-xs">元益有限公司 · 統編 60573934</p>
      </div>
    </div>
  );
}
